/**
 * Iter XX.115 (Feb 2026) — Acquisition-aware landing page config.
 *
 * ONE JSON object drives every bucket landing. To add a new bucket:
 *   1. Add an entry below keyed by the URL slug (also becomes the
 *      analytics `landing_page_id`).
 *   2. Add a route in `App.js`:
 *      <Route path="/{slug}" element={<InstantChatLanding landingPageId="{slug}" />} />
 *   3. Point the Meta ad at `https://askgully.com/{slug}`.
 *
 * The shape is intentionally small: only the things that must
 * change per bucket. Everything else on the landing page (layout,
 * demo animation cadence, composer behaviour, trust row, chip
 * grid, session bootstrap) stays shared across buckets.
 *
 * Editorial rules (founder-locked, Iter XX.110 principles):
 *   · The conversation IS the hero. Copy demonstrates coaching —
 *     it does not describe it.
 *   · Every demo conversation ends with a Gully turn that opens
 *     a live coaching question (not a summary) so the visitor's
 *     natural next action is to type their answer.
 *   · Placeholder text continues the conversation ("Tell Gully
 *     what happened in your last match…") — never a form label.
 *   · Every entry has 4-6 messages. No fewer (too short to prove
 *     value), no more (pushes composer below the fold on mobile).
 */

// The default bucket — MUST exactly match what XX.110 shipped for
// `/`. Adding new buckets does not change the homepage.
const HOME_DEMO = [
  { who: "you",   text: "I keep getting out driving." },
  { who: "gully", text: (
    "Same bowler, or different ones? "
    + "And are they balls you should be leaving?"
  )},
  { who: "you",   text: (
    "Different bowlers. Honestly… probably balls I could leave."
  )},
  { who: "gully", text: (
    "That tells me it’s probably not technique. "
    + "It’s decision-making under pressure. "
    + "What’s usually the score when it happens?"
  )},
];

const DEFAULT_PLACEHOLDER = "Tell me about your last match…";

// Iter XX.143 (Feb 2026) — Founder-locked strings.
//
// `WHAT_GULLY_DOES` is the same short one-liner every landing page
// shows immediately under its bucket-specific hook. It sets the
// expectation for what happens next in language that sounds like
// Gully already speaking to the visitor — not marketing copy.
//
// `AUDIENCE_LINE` reassures both players and parents that this page
// is for them. Shown identically on every bucket.
//
// `PLUS_SECONDARY_LABEL` — the quiet, non-competing link for warm
// visitors who already know Gully.
const HERO_INTRO = "Hi, I’m Gully.";
// Iter XX.145 — Founder-locked coach-voice line, same on every landing.
// The hero heading is bucket-specific (emotional hook); this line
// follows underneath as the coach's invitation to start talking.
const HERO_SUBLINE = "Let’s figure out what yours are saying.";
const PLUS_SECONDARY_LABEL = "Already know Gully? Explore Gully Plus →";

// Iter XX.120 (Feb 2026) — "Sell the journey" section.
// Renders BETWEEN the bucket demo/hero and the composer. Answers the
// emotional question the hero doesn't: "What happens if I start today?"
// Shape: heading + optional short intro line + 4 checkmark bullets +
// a soft cta line. Kept deliberately lightweight — this is a promise,
// not a marketing block. Every bucket ships its own copy so the
// promise reinforces the ad the visitor clicked.
const JOURNEY_CTA = "Start your first coaching conversation.";

// Audience tag lets the shared component pick the right starter
// chips: "player" (default) or "parent". Other UI stays identical.
const LANDING_PAGES = {
  home: {
    landing_page_id: "home",
    audience: "player",
    // Iter XX.145 — Curiosity-first heading. Founder-locked: "Every
    // match leaves clues." Works for players, parents, batters,
    // bowlers, and any campaign audience. The tag hero heading
    // creates an open loop (clues about what?) that only starting
    // a conversation can resolve.
    heading: "Every match leaves clues.",
    subheading: null,
    demo_conversation: HOME_DEMO,
    // Iter XX.145 — Placeholder encourages storytelling, not a
    // one-off "what happened" incident.
    composer_placeholder: "Tell me about your last match…",
    journey: {
      heading: "Your cricket journey starts here.",
      body_intro: "Over time Gully will help you understand:",
      bullets: [
        "Your recurring patterns",
        "What’s really shaping your cricket",
        "What helps you play your best",
        "How your game changes over time",
      ],
      // Iter XX.143 — Above-the-fold proof is now a single verified
      // number (`/api/public/landing-proof`). The below-the-fold
      // journey card no longer duplicates the same testimonial —
      // trustStory is only shown when the number falls below its
      // display floor and the frontend falls back to a quote.
      trustStory: {
        quote: (
          "I finally understood what shapes my cricket week to week."
        ),
        attribution: "Club cricketer",
      },
      cta_line: JOURNEY_CTA,
    },
  },

  performance: {
    landing_page_id: "performance",
    audience: "player",
    heading: "Playing well in nets, freezing in matches?",
    subheading:
      "Tell Gully what actually happens when the game starts.",
    demo_conversation: [
      { who: "you",   text: "I look totally different in matches vs. nets." },
      { who: "gully", text: (
        "That gap is almost never technique. "
        + "In your last match — what was the very first ball where you felt tight?"
      )},
      { who: "you",   text: "First over. Before I’d faced a ball. Just standing there felt heavy." },
      { who: "gully", text: (
        "So it’s already spiked before the bowler even runs in. "
        + "That points to what you’re carrying to the crease, "
        + "not what happens once you’re there."
      )},
      { who: "you",   text: "I was thinking about scoring and letting the team down the whole walk out." },
      { who: "gully", text: (
        "Right — you're batting the outcome, not the ball. "
        + "What do you usually tell yourself walking to the middle?"
      )},
    ],
    composer_placeholder: "Continue the conversation — what happens when you walk out to bat?",
    social_proof: (
      "Players who play very differently in matches usually have a " +
      "match-day head, not a match-day technique. Start there."
    ),
    journey: {
      heading: "Your performance journey starts here.",
      body_intro:
        "Today we’ll begin understanding why your training isn’t turning into performances. Over time Gully will help you discover:",
      bullets: [
        "Your recurring performance patterns",
        "What’s really holding you back",
        "What helps you perform your best",
        "How your cricket changes over time",
      ],
      trustStory: {
        quote:
          "I finally understood why my practice wasn’t showing up in matches.",
        attribution: "Club batter",
      },
      cta_line: JOURNEY_CTA,
    },
  },

  confidence: {
    landing_page_id: "confidence",
    audience: "player",
    heading: "Lost your cricket confidence?",
    subheading:
      "It doesn’t come back with a pep talk. Tell Gully what actually knocked it.",
    demo_conversation: [
      { who: "you",   text: "I’ve just lost belief in my game." },
      { who: "gully", text: (
        "When did that shift happen? "
        + "Was there a specific match or a run of games?"
      )},
      { who: "you",   text: "Two ducks in a row. Then a third. Now every innings feels like the fourth one." },
      { who: "gully", text: (
        "Yeah, confidence rarely leaks — it fractures. "
        + "Three quick failures rewires how you walk in. "
        + "What are you telling yourself in the last 30 seconds before facing your first ball now?"
      )},
      { who: "you",   text: "“Don’t get out first ball. Don’t look stupid.”" },
      { who: "gully", text: (
        "That’s the whole thing right there. "
        + "You’re playing to survive, not to score. "
        + "The technique still works — the intent has flipped."
      )},
    ],
    composer_placeholder: "Continue the conversation — when did your confidence actually shift?",
    journey: {
      heading: "Your confidence journey starts here.",
      body_intro: "Together we’ll understand:",
      bullets: [
        "When your confidence changes",
        "What’s actually causing it",
        "How to rebuild trust in your game",
        "How it improves over time",
      ],
      trustStory: {
        quote:
          "This is the first time I’ve felt someone actually understood what I was going through.",
        attribution: "Batter",
      },
      cta_line: JOURNEY_CTA,
    },
  },

  batting: {
    landing_page_id: "batting",
    audience: "player",
    heading: "Something in your batting isn’t working?",
    subheading:
      "Tell Gully what keeps happening. We’ll figure out what’s actually wrong.",
    demo_conversation: [
      { who: "you",   text: "I keep getting caught behind on the drive." },
      { who: "gully", text: "Full balls, or ones you should be leaving?" },
      { who: "you",   text: "Balls just outside off. Sometimes fourth or fifth stump." },
      { who: "gully", text: (
        "So it’s the leaveable ones. "
        + "Is it early in the innings, or after you’ve got in?"
      )},
      { who: "you",   text: "Almost always in the first 10 balls." },
      { who: "gully", text: (
        "That’s decision-making, not shot-making. "
        + "Your default in the first 10 needs to be leave/defend, "
        + "not drive. What’s pulling you into the drive early?"
      )},
    ],
    composer_placeholder: "Continue the conversation — what pulls you into that shot?",
    journey: {
      heading: "Your batting journey starts here.",
      body_intro: "Together we’ll uncover:",
      bullets: [
        "The dismissals that keep repeating",
        "What’s actually breaking your innings",
        "The decisions that unlock your batting",
        "How your batting evolves over time",
      ],
      trustStory: {
        quote:
          "I stopped getting out the same way because I finally saw the pattern.",
        attribution: "Opening batter",
      },
      cta_line: JOURNEY_CTA,
    },
  },

  bowling: {
    landing_page_id: "bowling",
    audience: "player",
    heading: "Bowling not doing what it did last season?",
    subheading:
      "Tell Gully what your last spell actually looked like. We’ll find the leak.",
    demo_conversation: [
      { who: "you",   text: "My line and length is all over the place." },
      { who: "gully", text: (
        "In every spell, or just under pressure? "
        + "Death overs, or from ball one?"
      )},
      { who: "you",   text: "It’s fine in the first two overs. Death overs it just falls apart." },
      { who: "gully", text: (
        "So it’s not your technique — your technique works "
        + "when you’re not thinking. In the death, what’s your "
        + "first thought as you walk back to your mark?"
      )},
      { who: "you",   text: "“Don’t bowl a full toss. Don’t get hit for six.”" },
      { who: "gully", text: (
        "Right — you’re bowling AWAY from bad balls "
        + "instead of TO your plan. That changes your run-up "
        + "tempo without you noticing. Do you have a specific "
        + "delivery you’re trying to hit at the death?"
      )},
    ],
    composer_placeholder: "Continue the conversation — what’s your death-overs plan?",
    journey: {
      heading: "Your bowling journey starts here.",
      body_intro: "Together we’ll work out:",
      bullets: [
        "The spells when you’re at your best",
        "What’s leaking under pressure",
        "Your plans, ball by ball",
        "How your bowling sharpens over time",
      ],
      trustStory: {
        quote:
          "After every spell I check in with Gully before changing anything.",
        attribution: "Leg spinner",
      },
      cta_line: JOURNEY_CTA,
    },
  },

  parents: {
    landing_page_id: "parents",
    audience: "parent",
    // Iter XX.118 — Widened the parents hero. The old copy jumped
    // straight into one specific scenario ("great start, now stuck");
    // Clarity showed parents arriving with many different problems
    // (improvement, confidence, batting, bowling, selection, motivation,
    // consistency) were bouncing because they didn't see themselves in
    // the demo. The new hero welcomes ALL parents first, then invites
    // them into coaching via curiosity — not diagnosis.
    heading: "Help your child become a better cricketer.",
    subheading:
      "Real coaching conversations that uncover what’s holding your child back — not generic parenting advice.",
    demo_conversation: [
      { who: "you",   text: "I feel like my child has stopped improving." },
      { who: "gully", text: (
        "What have you been noticing most recently? "
        + "Confidence, runs, bowling, or just something feels different?"
      )},
      { who: "you",   text: "They’re working hard but nothing seems to be changing." },
      { who: "gully", text: (
        "That’s actually more common than most parents realise. "
        + "Improvement usually stalls for a reason. "
        + "Let’s figure out what’s really happening."
      )},
    ],
    composer_placeholder:
      "Tell Gully what you’ve been noticing about your child’s cricket…",
    social_proof: (
      "The best support usually isn’t another drill — it’s " +
      "figuring out what’s actually stalling their progress."
    ),
    journey: {
      heading: "Your child’s cricket journey starts here.",
      body_intro: "Together we’ll discover:",
      bullets: [
        "What’s helping them improve",
        "Their confidence patterns",
        "Their recurring challenges",
        "How their cricket develops over time",
      ],
      trustStory: {
        quote:
          "Our conversations after matches are completely different now.",
        attribution: "Cricket parent",
      },
      cta_line: JOURNEY_CTA,
    },
  },

  selection: {
    landing_page_id: "selection",
    audience: "player",
    heading: "Overlooked at selection?",
    subheading:
      "Tell Gully what you’ve been putting in. We’ll figure out what’s not landing.",
    demo_conversation: [
      { who: "you",   text: "I keep getting overlooked despite scoring runs." },
      { who: "gully", text: (
        "What format, and what’s the runs breakdown? "
        + "Small consistent, or one big innings + failures?"
      )},
      { who: "you",   text: "T20s. Two 40s, but I've batted at four spots in five games." },
      { who: "gully", text: (
        "That’s selectors telling you they don’t know "
        + "where you fit yet. It’s not a runs problem, it’s "
        + "a definition problem. What role do you WANT?"
      )},
      { who: "you",   text: "I want to open, but they keep floating me." },
      { who: "gully", text: (
        "Then your job before the next selection is to make "
        + "the argument for opener impossible to ignore. "
        + "What’s the runs number that forces their hand?"
      )},
    ],
    composer_placeholder: "Continue the conversation — what have your last three innings looked like?",
    journey: {
      heading: "Your selection journey starts here.",
      body_intro: "Together we’ll figure out:",
      bullets: [
        "The role selectors haven’t seen you claim",
        "Why your runs aren’t landing yet",
        "The case you need to build",
        "How your standing changes over time",
      ],
      trustStory: {
        quote: "Getting dropped stopped feeling like the end.",
        attribution: "Club cricketer",
      },
      cta_line: JOURNEY_CTA,
    },
  },

  identity: {
    landing_page_id: "identity",
    audience: "player",
    heading: "Not sure what kind of cricketer you are anymore?",
    subheading:
      "Every serious cricketer hits this point. Tell Gully where you’re at.",
    demo_conversation: [
      { who: "you",   text: "I don’t even know what my game is right now." },
      { who: "gully", text: (
        "Fair. When you were playing your best cricket — "
        + "what did that version of you actually look like?"
      )},
      { who: "you",   text: "Aggressive opener. Backed myself to hit through the line." },
      { who: "gully", text: (
        "And now? Genuinely — describe how you’re batting."
      )},
      { who: "you",   text: "Cautious. Blocking early. Trying not to get out." },
      { who: "gully", text: (
        "So you’re not lost — you’ve been overwritten. "
        + "The aggressive version is still in there. "
        + "What made you switch to the cautious one?"
      )},
    ],
    composer_placeholder: "Continue the conversation — what made you change your game?",
    journey: {
      heading: "Your cricket identity starts here.",
      body_intro: "Together we’ll rebuild:",
      bullets: [
        "The cricketer you actually are",
        "What made you drift from your best",
        "The version worth rebuilding",
        "How your game evolves back over time",
      ],
      trustStory: {
        quote: "One bad season stopped defining me.",
        attribution: "Batter",
      },
      cta_line: JOURNEY_CTA,
    },
  },

  improvement: {
    landing_page_id: "improvement",
    audience: "player",
    heading: "Feel like your cricket has plateaued?",
    subheading:
      "Tell Gully what you’ve been working on. We’ll find where it’s stalling.",
    demo_conversation: [
      { who: "you",   text: "I train non-stop but my game isn’t moving." },
      { who: "gully", text: (
        "What does your usual training week actually look like?"
      )},
      { who: "you",   text: "3 nets, throwdowns twice, gym twice." },
      { who: "gully", text: (
        "That’s volume. What are you actually chasing in each "
        + "session — a specific score, shot, or pattern?"
      )},
      { who: "you",   text: "Just hitting balls, honestly." },
      { who: "gully", text: (
        "That’s the whole problem. Training without a "
        + "specific target reinforces exactly what you already "
        + "do. What’s the ONE thing in your batting you know "
        + "is costing you runs?"
      )},
    ],
    composer_placeholder: "Continue the conversation — what’s the one thing costing you runs?",
    journey: {
      heading: "Your improvement journey starts here.",
      body_intro: "Together we’ll unlock:",
      bullets: [
        "Where your training is stalling",
        "The one thing costing you runs",
        "Habits that unlock the next jump",
        "How your cricket compounds over time",
      ],
      trustStory: {
        quote:
          "I realised I wasn’t stuck because I wasn’t working hard.",
        attribution: "All-rounder",
      },
      cta_line: JOURNEY_CTA,
    },
  },

  situations: {
    landing_page_id: "situations",
    audience: "player",
    heading: "Same match situations keep breaking you?",
    subheading:
      "Powerplay, chase, middle overs — tell Gully which one you keep losing.",
    demo_conversation: [
      { who: "you",   text: "I always fold in a chase." },
      { who: "gully", text: (
        "What’s the moment it goes? Start of your innings, "
        + "or once the rate climbs?"
      )},
      { who: "you",   text: "Middle overs. When we need 7+ an over." },
      { who: "gully", text: (
        "So you’re fine while it’s a run-a-ball chase. "
        + "You break when the maths starts screaming. "
        + "What’s the first shot you try when the rate climbs?"
      )},
      { who: "you",   text: "Something big. Try to relieve pressure with a boundary." },
      { who: "gully", text: (
        "That’s the trap. Big shots in a climbing chase "
        + "are lottery tickets, not release. What was the last "
        + "chase you actually finished?"
      )},
    ],
    composer_placeholder: "Continue the conversation — walk me through your last chase.",
    journey: {
      heading: "Your match-day journey starts here.",
      body_intro: "Together we’ll master:",
      bullets: [
        "The situations that keep breaking you",
        "Why they break you",
        "How to survive them, then thrive in them",
        "How your match-play sharpens over time",
      ],
      trustStory: {
        quote:
          "Pressure stopped feeling random because I finally understood what was happening.",
        attribution: "Finisher",
      },
      cta_line: JOURNEY_CTA,
    },
  },

  "junior-parents": {
    landing_page_id: "junior-parents",
    audience: "parent",
    heading: "Backing your junior cricketer?",
    subheading:
      "Tell Gully what you’re seeing. Coaching insight built for the parent watching from the boundary.",
    demo_conversation: [
      { who: "you",   text: "My under-13 son is losing interest after tough weekends." },
      { who: "gully", text: (
        "How long has that been happening — a couple of games, "
        + "or a whole season?"
      )},
      { who: "you",   text: "Started around 6 weeks ago. Two low scores in a row." },
      { who: "gully", text: (
        "At that age, the drop-off after failure is usually "
        + "about how the FAILURE was talked about, not the "
        + "score itself. What did the car ride home look like?"
      )},
      { who: "you",   text: "Honest — I probably pushed too hard on what he could have done." },
      { who: "gully", text: (
        "You’re not alone there. Most parents do. The good "
        + "news is that’s the easiest thing to change. Do you "
        + "want to talk through what to say next time?"
      )},
    ],
    composer_placeholder: "Continue the conversation — describe how your child looks after a failure.",
    social_proof: (
      "Junior cricket confidence usually breaks on the car ride " +
      "home, not at the crease. Small changes there change everything."
    ),
    journey: {
      heading: "Your child’s cricket journey starts here.",
      body_intro: "Together we’ll discover:",
      bullets: [
        "What’s helping them love the game",
        "Their confidence in tough moments",
        "Their recurring challenges",
        "How their cricket develops over time",
      ],
      trustStory: {
        quote:
          "It’s helped me support my son without putting more pressure on him.",
        attribution: "Parent",
      },
      cta_line: JOURNEY_CTA,
    },
  },
};

export const HOME_LANDING_PAGE_ID = "home";
export const KNOWN_LANDING_PAGE_IDS = Object.keys(LANDING_PAGES);

// Iter XX.145 — Founder-locked shared strings (see comment above).
export const HERO_INTRO_LINE = HERO_INTRO;
export const HERO_SUBLINE_LINE = HERO_SUBLINE;
export const PLUS_SECONDARY_LINK_LABEL = PLUS_SECONDARY_LABEL;

/** Return the config for a given `landingPageId`, or the home config
 * if the id is unknown / missing. Always returns a full object. */
export function getLandingPageConfig(landingPageId) {
  const id = (landingPageId || HOME_LANDING_PAGE_ID).trim();
  return LANDING_PAGES[id] || LANDING_PAGES[HOME_LANDING_PAGE_ID];
}

export default LANDING_PAGES;
